import type { BadgeTone } from '@/components/ui/Badge';
import type { TeachingMethod } from '@/types/dailyReport';

/**
 * How the console describes a daily report.
 *
 * One source of truth for every label, so a method reads the same on the form,
 * in the table and on whatever screen shows it next (CLAUDE.md §3).
 */

export const TEACHING_METHOD_LABEL = {
  LECTURE: 'Lecture / Explanation',
  WORKSHEET: 'Worksheet / Practice',
  GROUP_ACTIVITY: 'Group Activity',
  DISCUSSION: 'Discussion',
  VIDEO: 'Video / Multimedia',
  OTHER: 'Other',
} as const satisfies Record<TeachingMethod, string>;

/** The order the form draws them in. Mirrors the server's own enum. */
export const TEACHING_METHODS = [
  'LECTURE',
  'WORKSHEET',
  'GROUP_ACTIVITY',
  'DISCUSSION',
  'VIDEO',
  'OTHER',
] as const satisfies readonly TeachingMethod[];

/**
 * How the four participation cards map onto the number that is stored.
 *
 * The column holds a percentage, a shape chosen when the table was made. The
 * form asks for a band instead, because "Good" is a judgement a teacher can
 * actually make about a room of thirty children and "81%" is not — nobody
 * counts participation, they weigh it.
 *
 * So the band is what is chosen and `percent` is what that band *means*, stated
 * once here rather than invented at each call site. `floor` reads the number
 * back into a band, and every band's percent sits inside its own floor, so a
 * value written as "Good" is always read back as "Good".
 *
 * Screens show the band, never the stored number: "80%" beside a lesson reads
 * as something measured, when all anybody chose was "Good".
 */
export interface ParticipationBand {
  value: 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'LOW';
  label: string;
  description: string;
  /** What choosing this band records. */
  percent: number;
  /** The lowest percentage that still reads as this band. Highest band first. */
  floor: number;
  /**
   * How the band is coloured wherever it is read back. Only "Low" is a
   * warning sign worth red — "Average" is a middling lesson, not a failing one.
   */
  tone: BadgeTone;
}

export const PARTICIPATION_BANDS: readonly ParticipationBand[] = [
  {
    value: 'EXCELLENT',
    label: 'Excellent',
    description: 'Very active and engaged',
    percent: 95,
    floor: 90,
    tone: 'success',
  },
  {
    value: 'GOOD',
    label: 'Good',
    description: 'Generally engaged',
    percent: 80,
    floor: 75,
    tone: 'info',
  },
  {
    value: 'AVERAGE',
    label: 'Average',
    description: 'Some participation',
    percent: 60,
    floor: 50,
    tone: 'warning',
  },
  {
    value: 'LOW',
    label: 'Low',
    description: 'Needs improvement',
    percent: 35,
    floor: 0,
    tone: 'critical',
  },
];

/**
 * Which band a stored percentage reads as, or null when nothing was recorded.
 *
 * Bands run highest first and the first floor the number clears wins — the same
 * shape `gradeFor` uses, so the two behave alike wherever both appear.
 */
export function participationBand(percent: number | null): ParticipationBand | null {
  if (percent === null) return null;

  return PARTICIPATION_BANDS.find((band) => percent >= band.floor) ?? null;
}

/** How many files one report may carry. The server enforces the same ceiling. */
export const MAX_ATTACHMENTS = 6;

/** What the drag-and-drop area accepts, matching the server's own list. */
export const ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

/** The ceiling per file, in megabytes — the API's `MAX_UPLOAD_MB`. */
export const MAX_ATTACHMENT_MB = 10;
